import { TDivisionEmployees, TModerators } from 'shared/types/types';
import { IndicatorKeys } from 'widgets/sidebar/menu/types';
import { edpApiV1 } from './index';

export type ViewerMeResponse = {
  id: number;
  email: string;
  employee: {
    id: number;
    name: string;
    middleName: string;
    lastName: string;
    personnelNumber: string;
    division: string;
    position: string;
    organization: string;
  };
  organization: { id: string; name: string };
  middleName: string | null;
  name: string | null;
  lastName: string;
  role: string | null;
  moderators?: TModerators;
  permissions: string[];
  expirationDate: string;
  dayForAttention: number | string;
};
export type ViewerOrganizationsResponse = [{ id: string | null; name: string | null }];
export type ViewerCurrentOrganizationRequest = { id: string };
export type ViewerCurrentOrganizationResponse = string;
export type ViewerSubordinatesRequest = { include?: string };
export type ViewerSubordinatesResponse = {
  id: number;
  name: string;
  middleName: string;
  lastName: string;
  organizationId: string;
  roleName: string;
}[];
export type ViewerDivisionsResponse = {
  data: {
    id: number;
    name: string;
    code: string;
  }[];
};
export type ViewerNotificationsResponse = {
  organizations: {
    organizationId: string;
    quantity: number;
  }[];
  sidebar: Record<IndicatorKeys, number>;
};

export type ViewerGetDivisionEmployeesResponse = {
  data: TDivisionEmployees[];
  pagination: {
    size: number;
    page: number;
    total: number;
  };
};

export type ViewerGetDivisionEmployeesRequest = {
  search?: string;
  divisionId?: string;
  page: number;
  size: number;
  allAvailableOrganizations: boolean;
};

export const viewerApi = edpApiV1.injectEndpoints({
  endpoints: build => ({
    me: build.query<ViewerMeResponse, null>({
      query: () => ({ url: '/v1/admin/viewer/me', method: 'GET' }),
      providesTags: ['viewer/me'],
    }),
    organizations: build.query<ViewerOrganizationsResponse, null>({
      query: () => ({ url: '/v1/admin/viewer/me/organizations', method: 'GET' }),
      providesTags: ['viewer/organizations'],
    }),
    getDivisionEmployees: build.query<ViewerGetDivisionEmployeesResponse, ViewerGetDivisionEmployeesRequest>({
      query: params => ({ url: '/v1/admin/viewer/me/employees', method: 'GET', params }),
      // keepUnusedDataFor: 0,
    }),
    setCurrentOrganization: build.mutation<ViewerCurrentOrganizationResponse, ViewerCurrentOrganizationRequest>({
      query: body => ({ url: '/v1/admin/viewer/me/set-current-organization', method: 'PATCH', body }),
      invalidatesTags: [
        'viewer/me',
        'references',
        'consultations',
        'vacations',
        'setDivision',
        'employees',
        'sendToZupCheck',
        'absence',
        'allowCheckScheduling',
      ],
    }),
    subordinates: build.query<ViewerSubordinatesResponse, ViewerSubordinatesRequest>({
      query: params => ({ url: '/v1/admin/subordinates', method: 'GET', params }),
      keepUnusedDataFor: 0,
    }),
    divisions: build.query<ViewerDivisionsResponse, null>({
      query: () => ({ url: '/v1/admin/viewer/me/divisions', method: 'GET' }),
      providesTags: ['setDivision'],
    }),
    indicators: build.query<ViewerNotificationsResponse, { allAvailableOrganizations: boolean }>({
      query: params => ({ url: '/v1/admin/viewer/me/notification', method: 'GET', params }),
    }),
  }),
  overrideExisting: false,
});

export const {
  useMeQuery,
  useLazyGetDivisionEmployeesQuery,
  useOrganizationsQuery,
  useLazyOrganizationsQuery,
  useSetCurrentOrganizationMutation,
  useSubordinatesQuery,
  useDivisionsQuery,
  useLazyDivisionsQuery,
  useIndicatorsQuery,
  useLazyIndicatorsQuery,
} = viewerApi;
